/**
 * Component: TeamManagement
 * Author: Dine AI
 * Date: July 16, 2025
 * Last Modified: December 17, 2025 - Light mode conversion
 * 
 * Purpose:
 * - Manages restaurant staff and waiter accounts
 * - Sends and tracks email invitations for waiters
 * - Displays active staff members with permissions
 * - Handles staff removal and invitation revocation
 * - Prepares for future mobile app integration
 * 
 * Props: None (uses restaurant context from auth)
 * 
 * State Management:
 * - Active waiters list from API
 * - Pending invitations with expiration tracking
 * - Modal states for various actions
 * - Form data for new invitations
 * 
 * Performance:
 * - Parallel API calls for data fetching
 * - Optimistic UI updates for better UX
 * - Debounced email validation
 * 
 * Error Handling:
 * - Email validation before sending
 * - Duplicate invitation prevention
 * - Clear error messages for failures
 * - Success confirmations for all actions
 */
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  UserPlus, 
  Mail, 
  Clock, 
  CheckCircle,
  XCircle,
  AlertCircle,
  Copy,
  Send,
  Trash2,
  UserCheck
} from 'lucide-react';
import { apiClient } from '@/services/api/apiClient';

interface WaiterInvitation {
  id?: number;
  invitation_id?: number;
  email?: string;
  waiter_email?: string;
  invitation_code: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  sent_at?: string;
  created_at?: string;
  expires_at: string;
  accepted_at?: string;
  waiter_name?: string;
}

interface ActiveWaiter {
  id: number;
  name: string;
  email: string;
  phone?: string;
  role: string;
  status: string;
  last_login_at?: string;
  login_count: number;
  member_since: string;
  permissions: string[];
}

const TeamManagement = () => {
  const [invitations, setInvitations] = useState<WaiterInvitation[]>([]);
  const [activeWaiters, setActiveWaiters] = useState<ActiveWaiter[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'staff' | 'invitations'>('staff');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [invitationToRevoke, setInvitationToRevoke] = useState<WaiterInvitation | null>(null);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [waiterToRemove, setWaiterToRemove] = useState<ActiveWaiter | null>(null);
  const [removing, setRemoving] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [waiterName, setWaiterName] = useState('');
  const [sending, setSending] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch invitations and active waiters on mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      console.log('[TeamManagement] Fetching staff and invitations data');
      const [invitationsResponse, waitersResponse] = await Promise.all([
        apiClient.get('/restaurant/waiters/invitations'),
        apiClient.get('/restaurant/waiters')
      ]);
      console.log('[TeamManagement] Data fetched:', { 
        invitations: invitationsResponse.data, 
        waiters: waitersResponse.data 
      });
      
      // Handle both array and object response formats for invitations
      const invitationData = Array.isArray(invitationsResponse.data) 
        ? invitationsResponse.data 
        : invitationsResponse.data.invitations || [];
      setInvitations(invitationData);
      
      // Handle waiters response
      const waitersData = Array.isArray(waitersResponse.data)
        ? waitersResponse.data
        : waitersResponse.data.waiters || [];
      setActiveWaiters(waitersData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvitations = async () => {
    try {
      const response = await apiClient.get('/restaurant/waiters/invitations');
      // Handle both array and object response formats
      const invitationData = Array.isArray(response.data) 
        ? response.data 
        : response.data.invitations || [];
      setInvitations(invitationData);
    } catch (error) {
      console.error('Failed to fetch invitations:', error);
    }
  };

  const handleSendInvite = async () => {
    if (!inviteEmail) {
      setError('Please enter an email address');
      return;
    }

    setError('');
    setSending(true);

    try {
      const response = await apiClient.post('/restaurant/waiters/invite', {
        waiter_email: inviteEmail,
        waiter_name: waiterName || undefined
      });

      setSuccess(`Invitation sent to ${inviteEmail}. They should receive an email shortly.`);
      setShowInviteModal(false);
      setInviteEmail('');
      setWaiterName('');
      
      // Refresh all data
      fetchData();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSending(false);
    }
  };

  const handleRevokeClick = (invitation: WaiterInvitation) => {
    setInvitationToRevoke(invitation);
    setShowRevokeModal(true);
  };

  const handleConfirmRevoke = async () => {
    if (!invitationToRevoke) return;

    setRevoking(true);
    try {
      const invitationId = invitationToRevoke.id || invitationToRevoke.invitation_id;
      await apiClient.post(`/restaurant/waiters/invitations/${invitationId}/revoke`);
      
      setShowRevokeModal(false);
      setInvitationToRevoke(null);
      fetchData();
      setSuccess('Invitation revoked successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Failed to revoke invitation:', error);
      setError('Failed to revoke invitation');
      setTimeout(() => setError(''), 3000);
    } finally {
      setRevoking(false);
    }
  };

  const copyInviteCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setSuccess('Invitation code copied to clipboard');
    setTimeout(() => setSuccess(''), 2000);
  };

  const handleRemoveClick = (waiter: ActiveWaiter) => {
    setWaiterToRemove(waiter);
    setShowRemoveModal(true);
  };

  const handleConfirmRemove = async () => {
    if (!waiterToRemove) return;

    setRemoving(true);
    try {
      await apiClient.delete(`/restaurant/waiters/${waiterToRemove.id}`);
      
      setShowRemoveModal(false);
      setWaiterToRemove(null);
      fetchData();
      setSuccess(`${waiterToRemove.name} has been removed from your restaurant`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Failed to remove waiter:', error);
      setError('Failed to remove staff member');
      setTimeout(() => setError(''), 3000);
    } finally {
      setRemoving(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'accepted':
        return <CheckCircle className="h-5 w-5 text-green-400" />;
      case 'expired':
        return <XCircle className="h-5 w-5 text-red-400" />;
      case 'revoked':
        return <XCircle className="h-5 w-5 text-gray-400" />;
      default:
        return <Clock className="h-5 w-5 text-yellow-400" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'Accepted';
      case 'expired':
        return 'Expired';
      case 'revoked':
        return 'Revoked';
      default:
        return 'Pending';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="text-center text-gray-600">Loading team data...</div>
      </div>
    );
  }

  // Filter invitations - only show pending, accepted, and expired (not revoked)
  const filteredInvitations = invitations.filter(inv => inv.status !== 'revoked');
  const pendingInvitations = filteredInvitations.filter(inv => inv.status === 'pending');
  const processedInvitations = filteredInvitations.filter(inv => inv.status !== 'pending');

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Team Management</h2>
            <p className="text-gray-600">
              Manage your restaurant staff and send invitations to new team members
            </p>
          </div>
          
          <button
            onClick={() => setShowInviteModal(true)}
            className="bg-[#FDB913] text-black rounded-lg px-4 py-2 font-medium hover:bg-[#FDB913]/90 transition-all flex items-center gap-2"
          >
            <UserPlus className="h-4 w-4" />
            Invite Staff Member
          </button>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <span className="text-green-700 text-sm">{success}</span>
          </div>
        )}

        {error && !showInviteModal && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <span className="text-red-700 text-sm">{error}</span>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('staff')}
            className={`flex-1 px-4 py-2 rounded-md font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'staff'
                ? 'bg-[#FDB913] text-black'
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-200'
            }`}
          >
            <Users className="h-4 w-4" />
            Active Staff ({activeWaiters.length})
          </button>
          <button
            onClick={() => setActiveTab('invitations')}
            className={`flex-1 px-4 py-2 rounded-md font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'invitations'
                ? 'bg-[#FDB913] text-black'
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-200'
            }`}
          >
            <Mail className="h-4 w-4" />
            Invitations ({pendingInvitations.length})
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'staff' ? (
          <div>
            {activeWaiters.length === 0 ? (
              <div className="bg-gray-50 rounded-lg p-6 text-center">
                <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600">
                  No active staff members yet
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  Send invitations to add team members to your restaurant
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {activeWaiters.map((waiter) => (
                  <motion.div
                    key={waiter.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gray-50 rounded-lg p-4 flex items-center justify-between hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#FDB913]/20 rounded-full flex items-center justify-center">
                        <span className="text-[#b8860b] font-semibold text-lg">
                          {waiter.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-gray-900 font-medium">{waiter.name}</p>
                        <p className="text-gray-500 text-sm">{waiter.email}</p>
                        <div className="flex items-center gap-4 mt-1">
                          <span className="text-gray-400 text-xs">
                            Joined {new Date(waiter.member_since).toLocaleDateString()}
                          </span>
                          {waiter.last_login_at && (
                            <span className="text-gray-400 text-xs">
                              Last login: {new Date(waiter.last_login_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <span className="text-sm px-3 py-1 rounded-full bg-green-100 text-green-700 capitalize">
                        {waiter.role}
                      </span>
                      <button
                        onClick={() => handleRemoveClick(waiter)}
                        className="text-red-500 hover:text-red-600 transition-colors p-2 hover:bg-red-50 rounded-lg"
                        title="Remove staff member"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div>
            {/* Pending Invitations */}
            {pendingInvitations.length > 0 && (
              <div className="mb-6">
                <h3 className="text-gray-700 font-medium mb-3 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Pending Invitations
                </h3>
                <div className="space-y-3">
                  {pendingInvitations.map((invitation) => (
                    <motion.div
                      key={invitation.id || invitation.invitation_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-gray-50 rounded-lg p-4 flex items-center justify-between hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#FDB913]/20 rounded-full flex items-center justify-center">
                          <Mail className="h-5 w-5 text-[#b8860b]" />
                        </div>
                        <div>
                          <p className="text-gray-900 font-medium">
                            {invitation.waiter_email || invitation.email}
                            {invitation.waiter_name && (
                              <span className="text-gray-500 ml-2">({invitation.waiter_name})</span>
                            )}
                          </p>
                          <div className="flex items-center gap-4 mt-1">
                            <p className="text-gray-500 text-sm">
                              Code: <span className="font-mono text-xs">{invitation.invitation_code}</span>
                            </p>
                            <button
                              onClick={() => copyInviteCode(invitation.invitation_code)}
                              className="text-[#b8860b] hover:text-[#FDB913] transition-colors"
                              title="Copy invitation code"
                            >
                              <Copy className="h-3 w-3" />
                            </button>
                          </div>
                          <p className="text-gray-400 text-xs mt-1">
                            Sent {new Date(invitation.created_at || invitation.sent_at).toLocaleDateString()}
                            • Expires {new Date(invitation.expires_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleRevokeClick(invitation)}
                          className="text-red-500 hover:text-red-600 transition-colors p-2 hover:bg-red-50 rounded-lg"
                          title="Revoke invitation"
                        >
                          <XCircle className="h-4 w-4" />
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Processed Invitations */}
            {processedInvitations.length > 0 && (
              <div>
                <h3 className="text-gray-700 font-medium mb-3 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Processed Invitations
                </h3>
                <div className="space-y-3">
                  {processedInvitations.map((invitation) => (
                    <motion.div
                      key={invitation.id || invitation.invitation_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-gray-50 rounded-lg p-4 opacity-60"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          invitation.status === 'accepted' ? 'bg-green-100' : 'bg-red-100'
                        }`}>
                          {invitation.status === 'accepted' ? (
                            <UserCheck className="h-5 w-5 text-green-600" />
                          ) : (
                            <XCircle className="h-5 w-5 text-red-500" />
                          )}
                        </div>
                        <div>
                          <p className="text-gray-900 font-medium">
                            {invitation.waiter_email || invitation.email}
                            {invitation.waiter_name && (
                              <span className="text-gray-500 ml-2">({invitation.waiter_name})</span>
                            )}
                          </p>
                          <p className="text-gray-400 text-xs mt-1">
                            {invitation.status === 'accepted'
                              ? `Accepted on ${new Date(invitation.accepted_at || invitation.sent_at).toLocaleDateString()}`
                              : `Expired on ${new Date(invitation.expires_at).toLocaleDateString()}`
                            }
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {filteredInvitations.length === 0 && (
              <div className="bg-gray-50 rounded-lg p-6 text-center">
                <Mail className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600">
                  No invitations sent yet
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  Click "Invite Staff Member" to get started
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl border border-gray-200 shadow-2xl p-6 max-w-md w-full"
          >
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Invite Staff Member</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm mb-2">
                  Staff Member Name (Optional)
                </label>
                <input
                  type="text"
                  value={waiterName}
                  onChange={(e) => setWaiterName(e.target.value)}
                  placeholder="John Smith"
                  className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#FDB913] focus:ring-1 focus:ring-[#FDB913]"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="staff@example.com"
                  className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#FDB913] focus:ring-1 focus:ring-[#FDB913]"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <span className="text-red-700 text-sm">{error}</span>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-blue-700 text-sm">
                  <strong>Note:</strong> The staff member will receive an email with instructions
                  to join your restaurant. They can use any OAuth provider to sign in.
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteEmail('');
                  setWaiterName('');
                  setError('');
                }}
                className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSendInvite}
                disabled={sending}
                className="flex-1 px-4 py-2 bg-[#FDB913] hover:bg-[#FDB913]/90 text-black rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {sending ? (
                  <span>Sending...</span>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Send Invitation
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Revoke Confirmation Modal */}
      {showRevokeModal && invitationToRevoke && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl border border-gray-200 shadow-2xl p-6 max-w-md w-full"
          >
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Revoke Invitation?</h3>

            <div className="mb-6">
              <p className="text-gray-600 mb-2">
                Are you sure you want to revoke this invitation?
              </p>
              <div className="bg-gray-50 rounded-lg p-3 mt-3">
                <p className="text-gray-900 font-medium">
                  {invitationToRevoke.waiter_email || invitationToRevoke.email}
                </p>
                {invitationToRevoke.waiter_name && (
                  <p className="text-gray-500 text-sm mt-1">{invitationToRevoke.waiter_name}</p>
                )}
              </div>
              <p className="text-gray-500 text-sm mt-3">
                This action cannot be undone. The invitation link will no longer work.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRevokeModal(false);
                  setInvitationToRevoke(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                disabled={revoking}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRevoke}
                disabled={revoking}
                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {revoking ? (
                  <span>Revoking...</span>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    Revoke Invitation
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Remove Staff Confirmation Modal */}
      {showRemoveModal && waiterToRemove && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl border border-gray-200 shadow-2xl p-6 max-w-md w-full"
          >
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Remove Staff Member?</h3>

            <div className="mb-6">
              <p className="text-gray-600 mb-2">
                Are you sure you want to remove this staff member?
              </p>
              <div className="bg-gray-50 rounded-lg p-3 mt-3">
                <p className="text-gray-900 font-medium">{waiterToRemove.name}</p>
                <p className="text-gray-500 text-sm mt-1">{waiterToRemove.email}</p>
              </div>
              <p className="text-gray-500 text-sm mt-3">
                They will no longer have access to your restaurant's waiter app.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRemoveModal(false);
                  setWaiterToRemove(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                disabled={removing}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRemove}
                disabled={removing}
                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {removing ? (
                  <span>Removing...</span>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    Remove Staff Member
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default TeamManagement;